import restful from '@/utils/restful';
import * as names from '@/store/names';
import * as get from '@/store/store';

const state = {
    users: [],
    users_url: '/authentication/user',
    // audiencias ya traídas por FETCH_USERS: 'staff' | 'students' | 'everyone'
    fetched_user_audiences: [],
    roles: [],
    roles_url: '/teaching/roles'

};

const getters = {
    [names.USERS]: state => {
        if (!get.store.state.user) return [];
        return state.users.filter((x) => x.school == get.store.state.user.school).sort((a, b) => a.id - b.id);
    },
    [names.ROLES]: state => {
        // TODO: No cuenta con filtro dado que es un dato estático en el Backend
        return state.roles;
    },
};

const mutations = {
    [names.MUTATE_USERS]: (state, users) => {
        users.forEach(element => {
            let index = state.users.findIndex(x => x.id == element.id);
            if (index != -1) state.users.splice(index, 1, element);
            else state.users.push(element);
        });
    },
    [names.MUTATE_USER]: (state, user) => {
        let index = state.users.findIndex(x => x.id == user.id);
        if (index != -1) state.users.splice(index, 1, user);
        else state.users.push(user);
    },
    [names.MUTATE_DELETE_USER]: (state, user_id) => {
        let index = state.users.findIndex(x => x.id == user_id);
        if (index != -1) state.users.splice(index, 1);
    },
    [names.MUTATE_FETCHED_USER_AUDIENCE]: (state, audience) => {
        if (!state.fetched_user_audiences.includes(audience))
            state.fetched_user_audiences.push(audience);
    },
    [names.MUTATE_ROLES]: (state, roles) => {
        roles.forEach(element => {
            let index = state.roles.findIndex(x => x.id == element.id);
            if (index != -1) state.roles.splice(index, 1, element);
            else state.roles.push(element);
        });
    },
};

// Requests de FETCH_USER en vuelo, para deduplicar GETs simultáneos del mismo id
const pending_user_requests = {};
// Requests de FETCH_USERS en vuelo, por audiencia (dos vistas montando a la vez)
const pending_audience_requests = {};
// Audiencia → nombres CANÓNICOS de grupos Django (globales, no los cargos por
// escuela). El backend los filtra vía groups__name__in (CustomUserFilter).
const AUDIENCE_GROUP_NAMES = {
    students: ['ESTUDIANTES'],
    staff: [
        'PROFESORES',
        'DIRECTORES DE CARRERA',
        'DIRECTIVOS FACULTAD O ESCUELA',
        'DIRECTIVOS ACADÉMICOS INSTITUCIONALES',
        'DIRECTIVOS ACADÉMICOS (CAMPUS)',
        'ADMINISTRATIVOS INSTRUCCIONALES',
    ],
};
AUDIENCE_GROUP_NAMES.everyone = [
    ...AUDIENCE_GROUP_NAMES.students,
    ...AUDIENCE_GROUP_NAMES.staff,
    'VISITANTES',
];

const actions = {
    [names.FETCH_USER]: ({
        commit,
        state
    }, payload) => {
        // Acepta un id directo o { user_id, force: true } para saltar el cache
        const user_id = payload instanceof Object ? payload.user_id : payload;
        const force = payload instanceof Object && payload.force === true;
        if (user_id == null) return Promise.resolve(null);
        if (!force) {
            const cached_user = state.users.find(x => x.id == user_id);
            if (cached_user) return Promise.resolve(cached_user);
            if (pending_user_requests[user_id]) return pending_user_requests[user_id];
        }
        const request = restful.Get(`${state.users_url}/${user_id}/`)
            .then(response => {
                delete pending_user_requests[user_id];
                commit(names.MUTATE_USER, response);
                return response;
            }, error => {
                delete pending_user_requests[user_id];
                throw error;
            });
        pending_user_requests[user_id] = request;
        return request;
    },
    [names.FETCH_USERS]: ({
        commit,
        state,
    }, payload) => {
        return new Promise((resolve) => {
            // Contrato F2: el endpoint de usuarios NUNCA se consulta sin acotar por
            // audiencia (groups__name__in con los nombres canónicos de grupos) —
            // los flags is_professor/is_student de la DB están deprecados.
            const audience = payload ? payload.audience : null;
            const school_id = get.store.state.user ? get.store.state.user.school : null;
            if (!['staff', 'students', 'everyone'].includes(audience) || school_id == null) {
                if (process.env.NODE_ENV !== 'production')
                    console.error('[all_data-guard] FETCH_USERS: requiere payload.audience (staff|students|everyone) y usuario con school — se resuelve [].');
                return resolve([]);
            }
            const force = payload && payload.force === true;
            if (!force && (state.fetched_user_audiences.includes(audience) ||
                state.fetched_user_audiences.includes('everyone'))) {
                return resolve(state.users);
            }
            if (!force && pending_audience_requests[audience]) {
                return resolve(pending_audience_requests[audience]);
            }
            const group_names = encodeURIComponent(AUDIENCE_GROUP_NAMES[audience].join(','));
            pending_audience_requests[audience] = restful.GetAll(
                `${state.users_url}/?groups__name__in=${group_names}&school__id=${school_id}&ordering=id`)
                .then((rows) => {
                    commit(names.MUTATE_USERS, rows);
                    commit(names.MUTATE_FETCHED_USER_AUDIENCE, audience);
                    delete pending_audience_requests[audience];
                    return state.users;
                });
            resolve(pending_audience_requests[audience]);
        });
    },
    [names.FETCH_ALT_USERS]: ({
        commit,
        state,
    }, payload) => {
        return new Promise((resolve) => {
            let filters = [];
            if (payload) {
                if (payload.user_ids) {
                    if (payload.user_ids.length > 0)
                        filters.push('id__in=' + payload.user_ids.join(","))
                    else return resolve([])

                }
                if (payload.groups_ids) {
                    if (payload.groups_ids.length > 0)
                        filters.push('groups__id__in=' + payload.groups_ids.join(","))
                    else return resolve([])
                }
            }
            if (filters.length === 0) {
                if (process.env.NODE_ENV !== 'production')
                    console.error('[all_data-guard] FETCH_ALT_USERS: dispatch sin filtro acotador — se resuelve [].');
                return resolve([]);
            }
            if (get.store.state.user && get.store.state.user.school != null)
                filters.push('school__id=' + get.store.state.user.school)
            filters.push('ordering=id')
            restful.GetAll(`${state.users_url}/?${filters.join('&')}`)
                .then(rows => {
                    commit(names.MUTATE_USERS, rows);
                    resolve(rows);
                });
        });
    },
    [names.POST_USER]: ({
        commit,
        state
    }, user) => {
        return new Promise((resolve) => {
            restful.Post(`${state.users_url}/`, user)
                .then(response => {
                    commit(names.MUTATE_USER, response);
                    resolve(response);
                });
        });
    },
    [names.UPDATE_USER]: ({
        commit,
        state
    }, user) => {
        return new Promise((resolve) => {
            restful.Put(`${state.users_url}/${user.id}/`, user)
                .then(response => {
                    commit(names.MUTATE_USER, response);
                    resolve(response);
                });
        });
    },
    [names.PATCH_USER]: ({
        commit,
        state
    }, payload) => {
        return new Promise((resolve) => {
            restful.Patch(`${state.users_url}/${payload.user_id}/`, payload.item)
                .then(response => {
                    commit(names.MUTATE_USER, response);
                    resolve(response);
                });
        });
    },
    [names.DELETE_USER]: ({
        commit,
        state
    }, user_id) => {
        return new Promise((resolve, reject) => {
            restful.Delete(`${state.users_url}/${user_id}/`)
                .then(response => {
                    commit(names.MUTATE_DELETE_USER, user_id);
                    resolve(response);
                })
                .catch(error => {
                    reject(error);
                });
        });
    },
    [names.FETCH_ROLES]: ({
        commit,
        state
    },) => {
        return new Promise((resolve) => {
            if (state.roles.length > 0) {
                resolve(state.roles);
                return;
            }
            restful.GetAll(`${state.roles_url}/`)
                .then(response => {
                    commit(names.MUTATE_ROLES, response);
                    resolve(response);
                });
        });
    },
};

export default {
    state,
    getters,
    mutations,
    actions,
};